import React from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import CBModal from '../../shared/CBModal';
import ChainMessageDetailModal from './ChainMessageDetailModal';
import {
  fetchVehicleMessageHistory as _fetchVehicleMessageHistory,
  approveMessage as _approveMessage,
  forwardMessage as _forwardMessage,
} from '../../../actions/messageActions';

// Window around the reference (maintenance / invoice) date: 1 month before -> 2 weeks after.
// Mirrors MESSAGE_HISTORY_WINDOW_* in chain/serializers.py (message_history_count) —
// change both together.
const WINDOW_BEFORE_MONTHS = 1;
const WINDOW_AFTER_DAYS = 14;

const badgeBase = {
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: 10,
  fontFamily: 'TitilliumWeb-Bold',
  display: 'inline-block',
  marginLeft: 4,
};

function renderMessageTypeBadge(message, t) {
  if (message.message_type === 'chain_message') {
    return <span style={{ ...badgeBase, backgroundColor: '#0077b6', color: '#fff' }}>{t('typeChainMessage')}</span>;
  }
  if (message.message_type === 'approval_request') {
    return <span style={{ ...badgeBase, backgroundColor: '#e67e22', color: '#fff' }}>{t('typeApprovalRequest')}</span>;
  }
  if (message.message_type === 'availability_inquiry') {
    return <span style={{ ...badgeBase, backgroundColor: '#2e86ab', color: '#fff' }}>{t('typeAvailabilityInquiry')}</span>;
  }
  if (message.message_type === 'customer_approval') {
    return <span style={{ ...badgeBase, backgroundColor: '#d35400', color: '#fff' }}>{t('typeCustomerApproval')}</span>;
  }
  return <span style={{ ...badgeBase, backgroundColor: 'rgba(0,0,0,0.12)', color: '#333' }}>{t('typeVehicleMessage')}</span>;
}

function renderApprovalStatusBadge(message, t) {
  const isApprovalType = message.message_type === 'approval_request'
    || message.message_type === 'customer_approval';
  if (!isApprovalType || !message.approval_status) {
    return null;
  }
  const badgeColors = {
    pending: { backgroundColor: '#e67e22', color: '#fff' },
    approved: { backgroundColor: '#27ae60', color: '#fff' },
    rejected: { backgroundColor: '#e74c3c', color: '#fff' },
  };
  const labels = {
    pending: t('approvalPending'),
    approved: t('approvalApproved'),
    rejected: t('approvalRejected'),
  };
  return (
    <span style={{ ...badgeBase, ...badgeColors[message.approval_status] }}>
      {labels[message.approval_status] || message.approval_status}
    </span>
  );
}

class VehicleMessageHistoryModal extends React.Component {
  state = {
    selectedMessageId: null,
    detailModalIsOpen: false,
  };

  componentDidMount() {
    if (this.props.modalIsOpen) {
      this.fetchHistory();
    }
  }

  componentDidUpdate(prevProps) {
    const justOpened = this.props.modalIsOpen && !prevProps.modalIsOpen;
    const prevRegs = (prevProps.registrationNumbers || []).join(',');
    const nextRegs = (this.props.registrationNumbers || []).join(',');
    const regsChanged = prevRegs !== nextRegs;
    const dateChanged = this.props.referenceDate !== prevProps.referenceDate;
    if (this.props.modalIsOpen && (justOpened || regsChanged || dateChanged)) {
      this.fetchHistory();
    }
  }

  getWindow = () => {
    const { referenceDate } = this.props;
    if (!referenceDate) {
      return { dateFrom: undefined, dateTo: undefined };
    }
    const ref = moment(referenceDate);
    return {
      dateFrom: ref.clone().subtract(WINDOW_BEFORE_MONTHS, 'month').format('YYYY-MM-DD'),
      dateTo: ref.clone().add(WINDOW_AFTER_DAYS, 'days').format('YYYY-MM-DD'),
    };
  };

  // The window in reader-facing form, so the dialog can say which period it
  // covers instead of just the reference date.
  getWindowDisplay = () => {
    const { referenceDate } = this.props;
    if (!referenceDate) {
      return null;
    }
    const ref = moment(referenceDate);
    return {
      from: ref.clone().subtract(WINDOW_BEFORE_MONTHS, 'month').format('DD.MM.YYYY'),
      to: ref.clone().add(WINDOW_AFTER_DAYS, 'days').format('DD.MM.YYYY'),
      ref: ref.format('DD.MM.YYYY'),
    };
  };

  fetchHistory = () => {
    const { fetchVehicleMessageHistory, chainId, registrationNumbers, referenceDate } = this.props;
    if (!chainId || !registrationNumbers || !registrationNumbers.length || !referenceDate) {
      return;
    }
    const w = this.getWindow();
    fetchVehicleMessageHistory(chainId, registrationNumbers, w.dateFrom, w.dateTo);
  };

  openDetail = (message) => {
    this.setState({ selectedMessageId: message.id, detailModalIsOpen: true });
  };

  closeDetail = () => {
    this.setState({ detailModalIsOpen: false });
  };

  getSelectedMessage = () => {
    const { vehicleHistory } = this.props;
    const id = this.state.selectedMessageId;
    if (!vehicleHistory || id == null) return null;
    return vehicleHistory.find((m) => m.id === id) || null;
  };

  getChainId = (message) => {
    const { chainId, selectedChainId } = this.props;
    return (message && message.chain_id) || chainId || selectedChainId;
  };

  handleApprove = () => {
    const { approveMessage } = this.props;
    const message = this.getSelectedMessage();
    if (!message) return;
    const cid = this.getChainId(message);
    if (approveMessage && cid) {
      approveMessage(message.id, cid);
    }
  };

  handleForward = (messageId, data) => {
    const { forwardMessage } = this.props;
    const cid = this.getChainId(this.getSelectedMessage());
    if (forwardMessage && cid) {
      return forwardMessage(messageId, cid, data);
    }
    return Promise.resolve();
  };

  renderRow = (message) => {
    const { t } = this.props;
    const sender = message.sender_context_display
      || message.sender_workshop_name
      || message.user_display;
    return (
      <div
        key={message.id}
        style={styles.row}
        className='opacity-on-hover'
        onClick={() => this.openDetail(message)}
      >
        <div style={styles.rowTop}>
          <span style={styles.rowDate}>{moment(message.created_at).format('DD.MM.YYYY HH:mm')}</span>
          <span>
            {renderMessageTypeBadge(message, t)}
            {renderApprovalStatusBadge(message, t)}
          </span>
        </div>
        <div style={styles.rowSubject}>
          {message.subject || t('noSubjectPlaceholder', { defaultValue: '(ei aihetta)' })}
        </div>
        {sender && <div style={styles.rowSender}>{sender}</div>}
      </div>
    );
  };

  render() {
    const {
      modalIsOpen,
      closeModal,
      title,
      overlayZIndex,
      vehicleHistory,
      vehicleHistoryStatus,
      chainMemberships,
      chainCareServices,
      t,
    } = this.props;
    const { detailModalIsOpen } = this.state;
    const selectedMessage = this.getSelectedMessage();

    const loading = vehicleHistoryStatus && vehicleHistoryStatus.loading;
    const messages = vehicleHistory || [];
    const windowDisplay = this.getWindowDisplay();

    return (
      <React.Fragment>
        <CBModal
          modalIsOpen={modalIsOpen}
          closeModal={closeModal}
          overlayZIndex={overlayZIndex}
          title={t('vehicleMessageHistory')}
          submitForm={closeModal}
          submitText={t('close', { ns: 'common', defaultValue: 'Sulje' })}
          isValid
          hideCancelButton
        >
          <div style={styles.body}>
            {title && <div style={styles.contextLabel}>{title}</div>}
            {windowDisplay && (
              <div style={styles.windowLabel}>{t('historyWindowLabel', windowDisplay)}</div>
            )}
            {loading && (
              <div style={styles.info}>{t('loadingText', { defaultValue: 'Ladataan...' })}</div>
            )}
            {!loading && messages.length === 0 && (
              <div style={styles.info}>
                {windowDisplay ? t('noMessagesInWindow', windowDisplay) : t('noMessagesPlain')}
              </div>
            )}
            {!loading && messages.map(this.renderRow)}
          </div>
        </CBModal>

        {detailModalIsOpen && selectedMessage && (
          <ChainMessageDetailModal
            message={selectedMessage}
            modalIsOpen={detailModalIsOpen}
            closeModal={this.closeDetail}
            overlayZIndex={overlayZIndex}
            approveMessage={this.handleApprove}
            forwardMessage={this.handleForward}
            chainMemberships={chainMemberships}
            chainCareServices={chainCareServices}
          />
        )}
      </React.Fragment>
    );
  }
}

const styles = {
  body: {
    padding: '0 24px 24px 24px',
    color: '#333',
  },
  contextLabel: {
    fontSize: 14,
    fontFamily: 'TitilliumWeb-SemiBold',
    color: '#0095da',
    marginBottom: 4,
  },
  windowLabel: {
    fontSize: 12,
    color: '#666',
    marginBottom: 16,
  },
  info: {
    fontSize: 14,
    color: '#999',
    padding: '16px 0',
  },
  row: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 10,
    cursor: 'pointer',
  },
  rowTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  rowDate: {
    fontSize: 12,
    color: '#666',
  },
  rowSubject: {
    fontSize: 14,
    fontFamily: 'TitilliumWeb-SemiBold',
    color: '#333',
    overflowWrap: 'break-word',
  },
  rowSender: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
  },
};

function mapStateToProps(state) {
  return {
    vehicleHistory: state.message.vehicleHistory,
    vehicleHistoryStatus: state.message.vehicleHistoryStatus,
    selectedChainId: state.chain.selectedChainId,
    chainMemberships: state.chain.chainMemberships,
    chainCareServices: state.chain.chainCareServices,
  };
}

export default connect(
  mapStateToProps,
  {
    fetchVehicleMessageHistory: _fetchVehicleMessageHistory,
    approveMessage: _approveMessage,
    forwardMessage: _forwardMessage,
  },
)(translate('MessageListView')(VehicleMessageHistoryModal));
